import {
    migrateCollidingUserKernels,
} from "./kernelStore.js";
import {
    migrateCollidingUserCanvasTemplates,
} from "./canvasTemplateStore.js";
import {
    getTenantViewCloudState,
    loadTenantViewCloud,
} from "./tenantViewCloudStore.js";
import { importLegacyTenantViews } from "./tenantViewLegacyImport.js";
import { recoverPendingTenantViewCreates } from "./tenantViewPendingCreateRecovery.js";
import { importTenantView } from "./tenantViewsApi.js";

function viewerOwnedCloudKernelIds(cloud) {
    if (cloud?.status !== "ready") return new Map();
    const ids = new Map();
    const ambiguous = new Set();
    for (const row of cloud.rows || []) {
        const metadata = row?.metadata;
        if (metadata?.kind !== "kernel" || !metadata.viewerOwned) continue;
        if (typeof metadata.name !== "string" || typeof metadata.id !== "string") continue;
        if (ids.has(metadata.name) && ids.get(metadata.name) !== metadata.id) {
            ambiguous.add(metadata.name);
        } else {
            ids.set(metadata.name, metadata.id);
        }
    }
    for (const name of ambiguous) ids.delete(name);
    return ids;
}

/**
 * Initializes the tenant-scoped Studio inventory.
 *
 * Recovery must precede legacy import: Create and Import have distinct
 * idempotency domains, so importing a staging row whose Create response was
 * lost could otherwise commit a duplicate object.
 */
export async function initializeTenantViewStudio({
    tenantId,
    userId,
    secretOverrideApprovals = {},
    cryptoImpl = globalThis.crypto,
} = {}) {
    if (!tenantId) throw new TypeError("tenantId is required for Studio startup");

    const initialCloud = await loadTenantViewCloud(tenantId);
    // Only the resolved tenant bucket has trustworthy provenance. The legacy
    // `default` bucket predates tenant scoping and must never be uploaded
    // automatically into whichever tenant happens to sign in next. Pending
    // Create recovery remains safe because its journal is tenant-scoped.
    migrateCollidingUserKernels(tenantId);
    migrateCollidingUserCanvasTemplates(tenantId);

    const recovery = await recoverPendingTenantViewCreates({ tenantId });
    const recoveredCloud = getTenantViewCloudState(tenantId);

    let legacyImport = null;
    if (userId) {
        const api = {
            importTenantView: (input) => importTenantView(input, {
                capabilities: initialCloud.capabilities,
            }),
        };
        const imported = await importLegacyTenantViews({
            tenantId,
            userId,
            bucketId: tenantId,
            api,
            cloudKernelIds: viewerOwnedCloudKernelIds(recoveredCloud),
            capabilities: initialCloud.capabilities,
            cryptoImpl,
            secretOverrideApprovals,
        });
        legacyImport = { ...imported, cloudStatus: recoveredCloud.status };
    }

    const cloud = await loadTenantViewCloud(tenantId, { force: true });
    return { cloud, legacyImport, recovery };
}
